// ─────────────────────────────────────────────────────────────────────────────
// Shared TopNav, CD's exact .topnav markup. Brand lockup (mark + wordmark +
// "Welcome back, <name>"), nav tabs, icon buttons, and the band switcher. The
// name + band come from the data layer (User); everything else is CD's static
// chrome. Empty: no name → "Welcome back"; no band → "Your band" + blank avatar.
//
// Shared by every React screen (Dashboard, Contacts, …). Tabs that map to a
// migrated route navigate via react-router NavLink, and the active tab is
// derived from the current route (NavLink adds is-active when matched). Tabs for
// future screens stay present but inert (plain <button>), same CD .nav-tab look.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { User } from '../../lib';
import { apiGet, canSeeBooking, getVisibleTabs, getCachedUser, effectiveRoleKey, isPro } from '../../lib';
import { BrandMark, BrandWordmark, MessageIcon, SettingsIcon, BrandProBadge } from './glyphs';
import { BandSwitcher } from './BandSwitcher';
import { JediSwitcher } from './JediSwitcher';
import { NotificationsBell } from './NotificationsBell';
import { SettingsDrawer } from './SettingsDrawer';
/* msg-lazy 2026-07-15 (Dev consult item): the messages overlay is a large
   chunk nobody sees until they click the envelope, it now loads (and runs
   its data fetches) on FIRST open instead of shipping in the boot bundle.
   After first open it stays mounted, so thread state and the close-refresh
   behavior are unchanged. */
const MessagesOverlay = lazy(() => import('../messages-sections/MessagesOverlay').then((m) => ({ default: m.MessagesOverlay })));

// Raw shapes of the three counts the Messages badge sums (same endpoints the
// overlay loads). Total = unread threads + pending friend requests + pending
// incoming show invites.
interface BadgeThread { kind?: string; unread_count?: number; to_band?: string; }
interface BadgeThreadsRes { success?: boolean; threads?: BadgeThread[] }
interface BadgeFriend { status?: string; to_band?: string }
interface BadgeFriendsRes { success?: boolean; friends?: BadgeFriend[] }
interface BadgeSharedRes { success?: boolean; dates?: unknown[]; shared_dates?: unknown[] }
/* consolidation 2026-07-14 (Dev consult): the worker composes the three badge
   reads into ONE /api/social/boot response, same payloads, same gates. */
interface SocialBootRes {
  success?: boolean;
  threads?: BadgeThreadsRes | null;
  friends?: BadgeFriendsRes | null;
  shared_dates?: BadgeSharedRes | null;
}

/**
 * The primary tabs in CD's order. `to` is the react-router path for migrated
 * screens (navigates + reflects active state); `null` marks a future screen that
 * stays present but inert.
 */
// The schedule tab is role-gated: the Booking-tier seats (owner, Band Member AV,
// Manager, Booking agent) get the full money "Booking" tab; everyone else gets
// the read-only "Calendar" tab. Exactly ONE of the two ever renders. Built per
// user in the component below so the rest of the order stays static.

export interface TopNavProps {
  readonly user: User | null;
}

export function TopNav({ user: userProp }: TopNavProps): JSX.Element {
  // Seed from the warm module cache so the role-gated nav tabs resolve on the
  // FIRST paint of every screen, otherwise getVisibleTabs(null) fails closed to
  // the band-member tab set and the nav flickers before /api/me resolves.
  const user = userProp ?? getCachedUser() ?? null;
  /* welcome-name 2026-07-17 (Ryan: "for people its fine just to use first
     name but for artist it should be full band name"), the band ROOT account
     IS the band: splitting "Relient K" to "Relient" mangles the identity.
     People (members, managers, crew) keep the friendly first name. */
  const fullName = user?.display_name || '';
  const isBandIdentity = !!user && (user.is_band_primary || String(user.role || '').toLowerCase() === 'artist');
  const firstName = isBandIdentity
    ? (user?.band_name || fullName)
    : (fullName ? fullName.split(/\s+/)[0] : '');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  /* msg-lazy 2026-07-15: latches true on first open, mounts the lazy overlay
     once and keeps it mounted so its state survives close/reopen. */
  const [msgEverOpened, setMsgEverOpened] = useState(false);
  useEffect(() => { if (messagesOpen) setMsgEverOpened(true); }, [messagesOpen]);
  const [msgTarget, setMsgTarget] = useState<{ bandSlug?: string; userId?: string; threadId?: string } | null>(null);
  const [unread, setUnread] = useState(0);

  // Any screen can deep-open the inbox to a conversation by dispatching
  // window 'ac:open-messages' with { bandSlug } or { userId } (e.g. the contact
  // profile "Message" buttons). Mirrors legacy openWithBand / openWithUser.
  useEffect(() => {
    const onOpen = (e: Event): void => {
      const detail = (e as CustomEvent).detail as { bandSlug?: string; userId?: string; threadId?: string } | undefined;
      setMsgTarget(detail || null);
      setMessagesOpen(true);
    };
    window.addEventListener('ac:open-messages', onOpen);
    return () => window.removeEventListener('ac:open-messages', onOpen);
  }, []);

  // Messages badge: sum unread threads + pending friend requests + incoming
  // show invites. Refetched on mount + whenever the overlay closes (so accepting
  // a request or reading a thread updates the dot). Band-scoped via apiGet.
  const refreshUnread = useCallback(() => {
    void (async () => {
      /* consolidation 2026-07-14 (Dev consult): ONE /api/social/boot call
         replaces threads + friends + shared-dates, and the old redundant
         /api/me is gone entirely (the user prop already carries band_name).
         Sum logic is unchanged; each missing part degrades to zero exactly
         like the old per-call .catch(() => null). */
      const boot = await apiGet<SocialBootRes>('auth', '/api/social/boot').catch(() => null);
      const threads = boot?.threads ?? null;
      const friends = boot?.friends ?? null;
      const shared = boot?.shared_dates ?? null;
      const list = (threads && threads.success && threads.threads) || [];
      const threadUnread = list.filter((t) => (t.unread_count || 0) > 0).length;
      const myBand = String((user?.band_name || getCachedUser()?.band_name || '')).trim().toLowerCase();
      const allFriends = (friends && friends.success && friends.friends) || [];
      const pendingFriends = allFriends.filter(
        (f) => f && f.status === 'pending' && String(f.to_band || '').trim().toLowerCase() === myBand,
      ).length;
      const sharedList = (shared && (shared.dates || shared.shared_dates)) || [];
      setUnread(threadUnread + pendingFriends + sharedList.length);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.band_name]);

  /* boot-perf 2026-07-14 (Dev request): the badge's four calls (/api/me
     again, /api/threads, /api/friends, /api/friends/shared-dates, ~380ms
     each) used to fire at boot alongside the dashboard's first-paint data,
     competing for the same connection pool. The badge is a dot, defer its
     first load until the boot rush is over. Overlay-close refreshes (below)
     stay instant. */
  useEffect(() => {
    const t = setTimeout(refreshUnread, 3500);
    return () => clearTimeout(t);
  }, [refreshUnread]);
  // Per-role page visibility (mirrors legacy ROLE_TABS). The Schedule tab is
  // labeled Booking (money group) or Calendar (no-money group) but is the SAME
  // page-key gated internally, exactly one renders per seat.
  const tabs = getVisibleTabs(user);
  const money = canSeeBooking(user);
  /* nav-ia 2026-07-27 (Ryan: "Massive change… drop menus") — the flat tab row
     becomes the grouped IA:
       Dashboard · Music▾(Releases/Streaming) · Content▾(Assets/Socials/
       Marketing) · Touring▾(Booking/Buses[Pro]/Venue Assets) · Antenna ·
       Contacts.
     URLs renamed flat: /music→/streaming, /content→/socials, production
     stack→/venue-assets (App.tsx keeps the old paths as redirects). A group
     renders when ≥1 child is visible for the role; leaf gates unchanged. */
  type NavLeaf = { label: string; to: string };
  type NavItem = { label: string; to?: string; children?: NavLeaf[] };
  const _proNav = isPro(user);
  const _roleKey = effectiveRoleKey(user);
  const _prodOnly = ['foh_engineer', 'monitor_engineer', 'tech'].includes(_roleKey);
  const NAV_TABS: NavItem[] = [
    { label: 'Dashboard', to: '/' },
    {
      label: 'Music',
      children: [
        ...(tabs.has('releases') ? [{ label: 'Releases', to: '/releases' }] : []),
        { label: 'Streaming', to: '/streaming' },
      ],
    },
    {
      label: 'Content',
      children: [
        /* Production crew's only asset surface is Venue Assets (Touring). */
        ...(tabs.has('assets') && !_prodOnly ? [{ label: 'Assets', to: '/assets' }] : []),
        ...(tabs.has('content') ? [{ label: 'Socials', to: '/socials' }] : []),
        ...(tabs.has('marketing') ? [{ label: 'Marketing', to: '/marketing' }] : []),
      ],
    },
    {
      label: 'Touring',
      children: [
        ...(tabs.has('schedule') ? [money ? { label: 'Booking', to: '/booking' } : { label: 'Calendar', to: '/calendar' }] : []),
        ...(_proNav ? [{ label: 'Buses', to: '/buses' }] : []),
        /* advance-docs rename 2026-07-28 (Ryan + Rob) — the surface is
           "Advance Docs" everywhere now (Ryan extended the rename to
           standard same-day). Route stays /venue-assets, bookmarks safe. */
        ...(tabs.has('assets') ? [{ label: 'Advance Docs', to: '/venue-assets' }] : []),
      ],
    },
    /* antenna-gate 2026-07-17 (Ryan) — owner, AV+ and roster orgs only. */
    ...(['artist', 'band_member_av', 'admin', 'jedi', 'manager', 'label'].includes(effectiveRoleKey(user))
      ? [{ label: 'Antenna', to: '/antenna' }] : []),
    ...(tabs.has('contacts') ? [{ label: 'Contacts', to: '/contacts' }] : []),
  ].filter((t) => t.to || (t.children && t.children.length > 0));
  const loc = useLocation();
  const [openDd, setOpenDd] = useState<string | null>(null);
  return (
    <header className="topnav">
      <div className="topnav__inner">
        {/* audit 2026-07-02: was a dead href="#", route home like the tabs. */}
        <NavLink className="brand" to="/" end aria-label="Artist CNTRL home">
          <span className="brand__mark">
            <BrandMark />
          </span>
          <span className="brand__lockup">
            <span className="brand__wordmark" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <BrandWordmark />
              {/* acp 2026-07-24 — Taylor's PRO chip (svg-81) rides the lockup for
                  ACP accounts only. isPro() covers real acp rows AND the jedi
                  "View plan" preview, so flipping the switcher shows it. 14px
                  vs the 11px wordmark row: intentional, a chip reads as a
                  badge when it's a touch taller; overflow is visible here. */}
              {isPro(user) && (
                <span aria-hidden="false" style={{ display: 'block', height: 14, flexShrink: 0 }}>
                  <BrandProBadge />
                </span>
              )}
            </span>
            <span className="brand__welcome">
              {firstName ? (
                <>
                  Welcome back, <b>{firstName}</b>
                </>
              ) : (
                'Welcome back'
              )}
            </span>
          </span>
        </NavLink>

        <nav className="nav-tabs" aria-label="Primary">
          {NAV_TABS.map((tab) => {
            if (tab.to) {
              return (
                <NavLink
                  key={tab.label}
                  to={tab.to}
                  end={tab.to === '/'}
                  className={({ isActive }) => 'nav-tab' + (isActive ? ' is-active' : '')}
                >
                  {tab.label}
                </NavLink>
              );
            }
            const kids = tab.children || [];
            const childActive = kids.some((k) => loc.pathname === k.to || loc.pathname.indexOf(k.to + '/') === 0);
            const open = openDd === tab.label;
            return (
              <div
                key={tab.label}
                className="nav-dd"
                onMouseEnter={() => setOpenDd(tab.label)}
                onMouseLeave={() => setOpenDd((cur) => (cur === tab.label ? null : cur))}
              >
                <button
                  className={'nav-tab nav-tab--dd' + (childActive ? ' is-active' : '')}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={open}
                  onClick={() => setOpenDd(open ? null : tab.label)}
                >
                  {tab.label}
                  <svg className="nav-dd__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                </button>
                {open && (
                  <div className="nav-dd__menu" role="menu">
                    {kids.map((k) => (
                      <NavLink
                        key={k.to}
                        to={k.to}
                        className={({ isActive }) => 'nav-dd__item' + (isActive ? ' is-active' : '')}
                        onClick={() => setOpenDd(null)}
                      >
                        {k.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="nav-right">
          {/* primary-no-inbox 2026-07-27 (Ryan: "just hide the inbox icon from
              primary accounts") — the band PRIMARY is an invite-only shell
              once the team is on; it gets no Messages surface at all. AV and
              every other seat keep it. Gate is primary-flag AND role='artist'
              so jedi/admin accounts are unaffected. */}
          {!(user?.is_band_primary && String(user?.role || '').toLowerCase() === 'artist') && (
          <button
            className={'iconbtn' + (messagesOpen ? ' is-active' : '')}
            type="button"
            aria-label="Messages"
            onClick={() => { setMsgTarget(null); setMessagesOpen(true); }}
          >
            {unread > 0 ? <span className="iconbtn__badge">{unread > 99 ? '99+' : unread}</span> : null}
            <MessageIcon />
          </button>
          )}
          <NotificationsBell />
          <button className="iconbtn" type="button" aria-label="Settings" onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </button>
          <JediSwitcher user={user} />
          <BandSwitcher user={user} />
        </div>
      </div>
      <SettingsDrawer user={user} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {msgEverOpened && (
        <Suspense fallback={null}>
          <MessagesOverlay open={messagesOpen} openTarget={msgTarget} user={user} onClose={() => { setMessagesOpen(false); setMsgTarget(null); refreshUnread(); }} />
        </Suspense>
      )}
    </header>
  );
}
